import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Fullscreen support for the share surface (the "expand the demo" affordance):
 *
 * - Desktop Chrome/Firefox and iPad Safari take the standard Element.requestFullscreen path.
 * - Older desktop Safari exposes only the webkit-prefixed requestFullscreen.
 * - iPhone Safari never fullscreens a non-video element; the only escape hatch is the native
 *   video player (HTMLVideoElement.webkitEnterFullscreen). That path shows Safari's own
 *   playback UI and exits through it, so the state tracking rides the webkitbegin/endfullscreen
 *   element events instead of the document ones.
 * - When neither path exists the surface reports the capability honestly and the tile shows a
 *   short best-effort hint instead of a dead control.
 */

/** How the current engine can fullscreen the share surface. */
export type SurfaceFullscreenMode = 'element' | 'native-video' | 'unavailable';

/** HTMLElement shape with the legacy webkit fullscreen entry (older desktop Safari). */
interface WebkitFullscreenElement extends HTMLElement {
  readonly webkitRequestFullscreen?: () => void;
}

/** Document shape with the legacy webkit fullscreen accessors. */
interface WebkitFullscreenDocument extends Document {
  readonly webkitFullscreenElement?: Element | null;
  readonly webkitExitFullscreen?: () => Promise<void> | void;
}

/** HTMLVideoElement shape with the iOS native player fullscreen entry. */
export interface NativeFullscreenVideo extends HTMLVideoElement {
  readonly webkitEnterFullscreen?: () => void;
  readonly webkitExitFullscreen?: () => void;
}

/**
 * The element currently holding fullscreen, through the standard or the webkit accessor.
 * jsdom and engines without the API answer null.
 */
export function fullscreenElementOf(doc: Document): Element | null {
  const doc2 = doc as WebkitFullscreenDocument;
  return doc2.fullscreenElement ?? doc2.webkitFullscreenElement ?? null;
}

/**
 * Capability snapshot for one surface: the standard/prefixed element path wins over the iOS
 * native video player; neither means the honest «unavailable» degrade.
 */
export function surfaceFullscreenMode(
  surface: HTMLElement | null,
  video: HTMLVideoElement | null,
): SurfaceFullscreenMode {
  if (surface !== null) {
    if (typeof surface.requestFullscreen === 'function') {
      return 'element';
    }
    if (typeof (surface as WebkitFullscreenElement).webkitRequestFullscreen === 'function') {
      return 'element';
    }
  }
  if (video !== null && typeof (video as NativeFullscreenVideo).webkitEnterFullscreen === 'function') {
    return 'native-video';
  }
  return 'unavailable';
}

/** Toggles itself out of fullscreen: standard first, webkit document accessor, native player. */
export function exitSurfaceFullscreen(video: HTMLVideoElement | null): void {
  if (fullscreenElementOf(document) !== null) {
    void document.exitFullscreen().catch(() => {
      // Best-effort: the user gesture may have expired; the state sync corrects the UI.
    });
    return;
  }
  const doc = document as WebkitFullscreenDocument;
  if (typeof doc.webkitExitFullscreen === 'function') {
    doc.webkitExitFullscreen();
    return;
  }
  const native = video as NativeFullscreenVideo | null;
  if (native !== null && typeof native.webkitExitFullscreen === 'function') {
    native.webkitExitFullscreen();
  }
}

/** How long the unsupported-hint stays on screen after a failed toggle. */
export const FULLSCREEN_HINT_MS = 2_600;

/** Tap-window for the double-tap fullscreen affordance on touch devices. */
export const DOUBLE_TAP_MS = 300;

export interface SurfaceFullscreen {
  /** True while THIS surface (or its native-video player) is the fullscreen element. */
  readonly active: boolean;
  /** Transient flag: the toggle found no supported path (the tile shows the rotate hint). */
  readonly unavailable: boolean;
  readonly toggle: () => void;
}

/**
 * Tracks and toggles fullscreen for one surface element (plus the surface's <video> for the
 * iOS native-player fallback). State stays honest both ways: the document-level sync reacts to
 * button-driven AND browser-driven (Escape, the native Done button) exits.
 */
export function useSurfaceFullscreen(
  surfaceRef: RefObject<HTMLElement | null>,
  videoRef: RefObject<HTMLVideoElement | null>,
): SurfaceFullscreen {
  const [elementActive, setElementActive] = useState(false);
  const [nativeActive, setNativeActive] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const activeRef = useRef(false);
  activeRef.current = elementActive || nativeActive;

  // Document-level sync (standard + webkit event names) and the iOS native-player element
  // events. The sync also runs once on mount so a surface mounted INTO an active fullscreen
  // session reflects it.
  useEffect(() => {
    const sync = (): void => {
      const element = fullscreenElementOf(document);
      const surface = surfaceRef.current;
      setElementActive(
        surface !== null && element !== null && (element === surface || surface.contains(element)),
      );
    };
    sync();
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    const video = videoRef.current;
    const onBegin = (): void => {
      setNativeActive(true);
    };
    const onEnd = (): void => {
      setNativeActive(false);
    };
    video?.addEventListener('webkitbeginfullscreen', onBegin);
    video?.addEventListener('webkitendfullscreen', onEnd);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      video?.removeEventListener('webkitbeginfullscreen', onBegin);
      video?.removeEventListener('webkitendfullscreen', onEnd);
    };
  }, [surfaceRef, videoRef]);

  // The unsupported-hint is transient: it clears itself so the tile returns to a clean state.
  useEffect(() => {
    if (!unavailable) {
      return;
    }
    const timer = window.setTimeout((): void => {
      setUnavailable(false);
    }, FULLSCREEN_HINT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [unavailable]);

  const toggle = useCallback((): void => {
    if (activeRef.current) {
      exitSurfaceFullscreen(videoRef.current);
      return;
    }
    const surface = surfaceRef.current;
    const video = videoRef.current;
    if (typeof surface?.requestFullscreen === 'function') {
      void surface.requestFullscreen().catch(() => {
        // The browser may refuse (expired gesture, iframe policy): nothing to surface, the
        // state sync keeps the button honest.
      });
      return;
    }
    const webkit = surface as WebkitFullscreenElement | null;
    if (webkit !== null && typeof webkit.webkitRequestFullscreen === 'function') {
      webkit.webkitRequestFullscreen();
      return;
    }
    const native = video as NativeFullscreenVideo | null;
    if (native !== null && typeof native.webkitEnterFullscreen === 'function') {
      native.webkitEnterFullscreen();
      return;
    }
    setUnavailable(true);
  }, [surfaceRef, videoRef]);

  return { active: elementActive || nativeActive, unavailable, toggle };
}
